import type { CmmdWorkSession } from "@cmmd-forge/contracts";
import { useCallback, useEffect, useState } from "react";

import { createCmmdApiClient } from "~/environments/cmmdApi";
import { sanitizePublicUiError } from "~/productSafeLabels";

const api = createCmmdApiClient();

export function useProjectWorkSessions(projectId: string) {
  const [sessions, setSessions] = useState<readonly CmmdWorkSession[]>([]);
  const [allSessions, setAllSessions] = useState<readonly CmmdWorkSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await api.listProjectWorkSessions(projectId);
      setAllSessions(next);
      setSessions(
        next.filter((session) => session.status === "starting" || session.status === "active"),
      );
      return next;
    } catch (cause) {
      setError(sanitizePublicUiError(cause, "Could not load active work."));
      return [];
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setSessions([]);
    setAllSessions([]);
    void reload();
  }, [reload]);

  return {
    sessions,
    allSessions,
    loading,
    error,
    reload,
  };
}

export type ProjectWorkSessionsState = ReturnType<typeof useProjectWorkSessions>;
